import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  TextField,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useConfirm } from "material-ui-confirm";
import { postNotification } from "../../../../components/utilities/Notifications";
import {
  notifyRandomToolError,
  randomScoresBody,
  submitRandomScores
} from "../../utils/randomTools";

// Dialog for "Submit Random ... Matches": fills a round with random but valid scores (BCP-529). Max score is
// required because the scorecards do not carry one; the API answers maxScoreRequired if one ever has to be sent.
export default function RandomScoresModal(props) {
  const { open, setOpen, event, roundNo, pairingType, onDone } = props;
  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventPairings"
  });
  const confirm = useConfirm();

  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setValues({
        round: roundNo || event?.status?.currentRound || "",
        scoreByes: true,
        resubmitScores: false,
        minScore: "",
        maxScore: ""
      });
    }
  }, [open, roundNo, event?.status?.currentRound]);

  const set = (key) => (e) =>
    setValues({
      ...values,
      [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value
    });

  const round = Number(values.round);
  const min = values.minScore === "" ? undefined : Number(values.minScore);
  const max = Number(values.maxScore);
  const errors = {
    round: !Number.isInteger(round) || round < 1 ? t("randomScoresRoundInvalid") : "",
    maxScore:
      values.maxScore === "" || !(max > 0)
        ? t("randomScoresMaxRequired")
        : min !== undefined && !(max > min)
          ? t("randomScoresMaxAboveMin")
          : "",
    minScore:
      min !== undefined && (!Number.isFinite(min) || min < 0)
        ? t("randomScoresMinInvalid")
        : ""
  };
  const valid = !errors.round && !errors.maxScore && !errors.minScore;

  const submit = async () => {
    try {
      await confirm({
        title: t("randomScoresConfirmTitle"),
        description: values.resubmitScores
          ? t("randomScoresConfirmResubmit", { round })
          : t("randomScoresConfirm", { round }),
        dialogProps: { sx: { zIndex: 99999 } }
      });
    } catch (err) {
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitRandomScores(
        randomScoresBody({ eventId: event.id, pairingType, values })
      );
      postNotification(
        "success",
        t("randomScoresResult", {
          scored: result.scored,
          total: result.total,
          alreadyDone: result.skipped?.alreadyDone || 0,
          byes: result.skipped?.byes || 0
        })
      );
      if (result.failed?.length) {
        postNotification(
          "warning",
          t("randomScoresFailed", {
            count: result.failed.length,
            first: `${t("table")} ${result.failed[0].table}: ${result.failed[0].error}`
          })
        );
      }
      setOpen(false);
      if (onDone) onDone();
    } catch (err) {
      notifyRandomToolError(err);
    }
    setSubmitting(false);
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={() => !submitting && setOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t("randomScoresTitle")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("randomScoresDescription")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="number"
              label={t("round")}
              value={values.round ?? ""}
              onChange={set("round")}
              error={Boolean(errors.round)}
              helperText={errors.round}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="number"
              label={t("randomScoresMin")}
              value={values.minScore ?? ""}
              onChange={set("minScore")}
              error={Boolean(errors.minScore)}
              helperText={errors.minScore || t("randomScoresMinHelp")}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              required
              type="number"
              label={t("randomScoresMax")}
              value={values.maxScore ?? ""}
              onChange={set("maxScore")}
              error={Boolean(errors.maxScore) && values.maxScore !== ""}
              helperText={values.maxScore !== "" ? errors.maxScore : ""}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.scoreByes !== false}
                  onChange={set("scoreByes")}
                />
              }
              label={t("randomScoresScoreByes")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.resubmitScores === true}
                  onChange={set("resubmitScores")}
                />
              }
              label={t("randomScoresResubmit")}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)} disabled={submitting}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={submit}
          disabled={!valid || submitting}
        >
          {t("randomScoresSubmit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
